import React, { useCallback, useEffect, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import {
  fetchMyRedEnvelopeRounds,
  type RedEnvelopeMyRound,
  type RedEnvelopePaymentType,
} from '../../services/redEnvelopeService'
import { HistoryCollapseCard, type HistoryDetailRow } from './HistoryCollapseCard'
import type { HistoryOutletContext } from './ModernHistoryLayout'

const PAGE_SIZE = 20

const formatNumber = (value: number | null | undefined, digits = 0) =>
  Number(value || 0).toLocaleString('th-TH', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })

const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('th-TH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const unitFor = (paymentType: RedEnvelopePaymentType | string) =>
  paymentType === 'cash' ? 'บาท' : 'คะแนน'

const digitsFor = (paymentType: RedEnvelopePaymentType | string) =>
  paymentType === 'cash' ? 2 : 0

const formatBalancePair = (
  before: number | null | undefined,
  after: number | null | undefined,
  paymentType: string,
) => {
  if (before == null && after == null) return null
  const d = digitsFor(paymentType)
  const fmt = (n: number | null | undefined) => (n == null ? '—' : formatNumber(n, d))
  return `${fmt(before)} → ${fmt(after)} ${unitFor(paymentType)}`
}

const statusLabel = (status: string) => {
  if (status === 'completed') return 'จบรอบ'
  if (status === 'active') return 'กำลังเล่น'
  if (status === 'cancelled') return 'ยกเลิก'
  return status
}

const buildRows = (item: RedEnvelopeMyRound): HistoryDetailRow[] => {
  const unit = unitFor(item.paymentType)
  const digits = digitsFor(item.paymentType)
  const rows: HistoryDetailRow[] = [
    { label: 'โหมด', value: `${item.modeKey} · ${item.envelopeCount} ซอง` },
    { label: 'ช่องทาง', value: item.paymentType === 'cash' ? 'เงินสด' : 'คะแนน' },
    { label: 'สถานะ', value: statusLabel(item.status) },
    { label: 'ค่าเข้ารอบ', value: `${formatNumber(item.costAmount, digits)} ${unit}` },
    {
      label: 'ได้จริง',
      value: `${formatNumber(item.creditedTotal, digits)} ${unit}`,
      tone: item.creditedTotal > 0 ? 'positive' : 'default',
    },
    {
      label: 'สุทธิ',
      value: `${formatNumber(item.creditedTotal - item.costAmount, digits)} ${unit}`,
      tone: item.creditedTotal - item.costAmount >= 0 ? 'positive' : 'negative',
    },
  ]

  const debitPair = formatBalancePair(
    item.debitBalance?.before,
    item.debitBalance?.after,
    item.paymentType,
  )
  if (debitPair) {
    rows.push({ label: 'คงเหลือหลังหักค่าเข้า', value: debitPair })
  }

  const refundPair = formatBalancePair(
    item.refundBalance?.before,
    item.refundBalance?.after,
    item.paymentType,
  )
  if (refundPair) {
    rows.push({ label: 'คงเหลือหลังคืนยอด', value: refundPair })
  }

  const credited = (item.cells || []).filter((c) => c.credited)
  if (credited.length > 0) {
    rows.push({
      label: 'ซองที่ได้จริง',
      value: credited
        .map((c) => `#${c.index} ${formatNumber(c.amount, digits)}${item.paymentType === 'cash' ? '฿' : 'pts'}`)
        .join(' · '),
    })
  }

  rows.push({ label: 'เวลาเริ่มรอบ', value: formatDateTime(item.createdAt) })
  return rows
}

/** แท็บประวัติเกมส์ซองแดง — อยู่กลุ่มเดียวกับ BTD ที่ /history */
const HistoryRedEnvelopePage: React.FC = () => {
  useOutletContext<HistoryOutletContext>()
  const [items, setItems] = useState<RedEnvelopeMyRound[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadHistory = useCallback(async (pageNum = 1) => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetchMyRedEnvelopeRounds({ page: pageNum, limit: PAGE_SIZE })
      setItems(response.data || [])
      setPage(response.pagination?.page || pageNum)
      setTotalPages(Math.max(1, response.pagination?.totalPages || 1))
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'โหลดประวัติไม่สำเร็จ')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadHistory(1)
  }, [loadHistory])

  const handleCopyRef = async (refId: string) => {
    try {
      await navigator.clipboard.writeText(refId)
    } catch {
      // ignore
    }
  }

  return (
    <div className="htd-hist__stack">
      <h2 className="htd-hist__page-title">ประวัติเกมส์ซองแดง</h2>

      <div className="htd-hist__panel">
        <div className="htd-hist__summary">
          <div>
            <strong>รอบที่เล่นซองแดง</strong>
            <span>ค่าเข้า · ยอดได้จริง · ยอดคงเหลือก่อน→หลัง</span>
          </div>
          <div className="text-end">
            <Link to="/points/games/red-envelope" className="htd-hist__inline-link">
              ไปเล่นซองแดง
            </Link>
          </div>
        </div>
      </div>

      <div className="htd-hist__list">
        {loading && (
          <div className="htd-hist__loading">
            <h4>
              <i className="fas fa-spinner fa-spin me-2" />
              กำลังโหลดประวัติซองแดง...
            </h4>
          </div>
        )}

        {!loading && error && (
          <div className="htd-hist__shell">
            <h4>{error}</h4>
            <button type="button" className="htd-hist__retry-btn" onClick={() => void loadHistory(page)}>
              ลองโหลดอีกครั้ง
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="htd-hist__empty">
            <h4>ยังไม่มีประวัติซองแดง</h4>
            <p>เมื่อเริ่มเล่นรอบซองแดง รายการจะแสดงที่นี่</p>
            <Link to="/points/games/red-envelope" className="htd-hist__inline-link">
              ไปเล่นซองแดง
            </Link>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="htd-hist__panel mb-2">
              <div className="htd-hist__summary">
                <div>
                  <strong>
                    พบ {items.length} รายการ
                    {totalPages > 1 ? ` (หน้า ${page}/${totalPages})` : ''}
                  </strong>
                  <span>แตะการ์ดเพื่อดูรายละเอียด</span>
                </div>
              </div>
            </div>

            {items.map((item) => {
              const leftIcon =
                item.status === 'cancelled'
                  ? 'reject'
                  : item.status === 'active'
                    ? 'pending'
                    : 'success'
              const digits = digitsFor(item.paymentType)
              return (
                <HistoryCollapseCard
                  key={item.roundId}
                  id={item.roundId}
                  leftIcon={leftIcon}
                  refId={item.roundId}
                  onCopyRef={() => void handleCopyRef(item.roundId)}
                  rows={buildRows(item)}
                  header={(
                    <div className="w-100 pe-md-4">
                      <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>
                        {item.modeKey} · ได้ {formatNumber(item.creditedTotal, digits)}{' '}
                        {unitFor(item.paymentType)}
                      </div>
                      <small style={{ color: 'rgba(186,230,253,0.75)', display: 'block' }}>
                        ค่าเข้า {formatNumber(item.costAmount, digits)} {unitFor(item.paymentType)} ·{' '}
                        {statusLabel(item.status)} · {formatDateTime(item.createdAt)}
                      </small>
                    </div>
                  )}
                />
              )
            })}

            {totalPages > 1 && (
              <div className="d-flex justify-content-center gap-2 mt-2">
                <button
                  type="button"
                  className="htd-hist__retry-btn"
                  disabled={page <= 1}
                  onClick={() => void loadHistory(page - 1)}
                >
                  ก่อนหน้า
                </button>
                <button
                  type="button"
                  className="htd-hist__retry-btn"
                  disabled={page >= totalPages}
                  onClick={() => void loadHistory(page + 1)}
                >
                  ถัดไป
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default HistoryRedEnvelopePage
